/**
 * Minimal Markdown → HTML converter for AI-authored content (task descriptions,
 * document bodies). Covers the constructs the assistant actually emits:
 * headings, bold/italic/inline-code, links, fenced code blocks, unordered and
 * ordered lists, task lists (`- [ ]` / `- [x]`), blockquotes, horizontal rules,
 * and paragraphs. Output is fed
 * through sanitizeRichTextHtml before storage, so this only needs to produce
 * reasonable structure, not defend against untrusted input.
 *
 * Not a full CommonMark implementation — nested lists and tables are rendered
 * flat. Good enough for notes/plans/documentation.
 *
 * Raw HTML is allowed, as in CommonMark / GitHub Markdown (05.10.2026): agents
 * often send HTML where Markdown is expected, and escaping it stored the tags
 * as visible text ("<p>…</p>" on 20 board cards). So
 *   - a line that STARTS with a block-level tag (`<p>`, `<ul>`, `<h2>`, …)
 *     begins an HTML block that runs to the next blank line and is kept as is;
 *   - known inline tags (`<code>`, `<strong>`, `<a href>`, `<br>`, …) inside
 *     Markdown text are kept; any other `<…>` stays visible text.
 * Every caller runs the result through sanitizeRichTextHtml, which removes
 * anything unsafe (scripts, handlers, unknown tags) exactly as for HTML input.
 * Code spans and fenced code blocks still escape everything.
 */

// Private-use-area sentinels so a restored code-span index can't collide with
// real text (e.g. a standalone number). These code points never appear in
// Markdown input.
const CODE_OPEN = String.fromCharCode(0xe000);
const CODE_CLOSE = String.fromCharCode(0xe001);
const TAG_OPEN = String.fromCharCode(0xe002);
const TAG_CLOSE = String.fromCharCode(0xe003);

/** A line opening an HTML block (CommonMark "HTML block" start, block tags). */
const HTML_BLOCK_START = /^\s{0,3}<\/?(p|div|ul|ol|li|h[1-6]|table|thead|tbody|tfoot|tr|td|th|pre|blockquote|hr|br|figure|figcaption|img|section|article|details|summary|dl|dt|dd)(\s|>|\/>|$)/i;

/** `data-enc` / `data-enc-key` with any (or no) value — same shape as mcp/mediaKeys.js. */
const MEDIA_KEY_ATTR = /\s*\bdata-enc(?:-key)?(?=[\s=/>]|$)(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'>]+))?/gi;

/** Inline tags kept inside Markdown text (the rest of `<…>` stays visible). */
const INLINE_TAG = /<\/?(code|strong|b|em|i|a|br|kbd|sub|sup|s|del|u|mark|span)(\s[^<>]*)?\s*\/?>/gi;

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Inline: code spans first (so their contents aren't further processed), then
// bold, italic, links.
function inline(text) {
  const codeSpans = [];
  let out = text.replace(/`([^`]+)`/g, (_, code) => {
    codeSpans.push(`<code>${escapeHtml(code)}</code>`);
    return `${CODE_OPEN}${codeSpans.length - 1}${CODE_CLOSE}`;
  });
  // Known inline tags are set aside before escaping, so neither the escape
  // nor the emphasis rules below touch them (an `_` in an href stays an `_`).
  const tags = [];
  out = out.replace(INLINE_TAG, (tag) => {
    tags.push(tag);
    return `${TAG_OPEN}${tags.length - 1}${TAG_CLOSE}`;
  });
  out = escapeHtml(out);
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  out = out.replace(/(^|[^_])_([^_\n]+)_/g, '$1<em>$2</em>');
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, href) => `<a href="${escapeHtml(href)}">${label}</a>`);
  // Restore code spans (already escaped) and the kept inline tags.
  out = out.replace(new RegExp(`${CODE_OPEN}(\\d+)${CODE_CLOSE}`, 'g'), (_, i) => codeSpans[Number(i)]);
  out = out.replace(new RegExp(`${TAG_OPEN}(\\d+)${TAG_CLOSE}`, 'g'), (_, i) => tags[Number(i)]);
  return out;
}

export function markdownToHtml(md) {
  if (!md) return '';
  const lines = String(md).replace(/\r\n?/g, '\n').split('\n');
  const html = [];
  let i = 0;
  let listType = null; // 'ul' | 'ol' | 'check' | null

  const closeList = () => {
    if (listType) {
      html.push(listType === 'check' ? '</ul>' : `</${listType}>`);
      listType = null;
    }
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    const fence = line.match(/^```(\w*)\s*$/);
    if (fence) {
      closeList();
      const code = [];
      i += 1;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        code.push(lines[i]);
        i += 1;
      }
      i += 1; // consume closing fence
      html.push(`<pre><code>${escapeHtml(code.join('\n'))}</code></pre>`);
      continue;
    }

    // Blank line
    if (/^\s*$/.test(line)) {
      closeList();
      i += 1;
      continue;
    }

    // Raw HTML block: kept as is up to the next blank line (see the header).
    if (HTML_BLOCK_START.test(line)) {
      closeList();
      const block = [];
      while (i < lines.length && !/^\s*$/.test(lines[i])) {
        block.push(lines[i]);
        i += 1;
      }
      // Image keys never come in through Markdown (only the attachment path
      // sets them, mediaKeys.js): drop any `data-enc` / `data-enc-key`.
      html.push(block.join('\n').replace(MEDIA_KEY_ATTR, ''));
      continue;
    }

    // Heading
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      closeList();
      const level = heading[1].length;
      html.push(`<h${level}>${inline(heading[2].trim())}</h${level}>`);
      i += 1;
      continue;
    }

    // Horizontal rule
    if (/^\s*([-*_])\1\1+\s*$/.test(line)) {
      closeList();
      html.push('<hr>');
      i += 1;
      continue;
    }

    // Blockquote
    const quote = line.match(/^>\s?(.*)$/);
    if (quote) {
      closeList();
      html.push(`<blockquote>${inline(quote[1])}</blockquote>`);
      i += 1;
      continue;
    }

    // GFM pipe table: a header row followed by a |---|---| separator row.
    const isTableSep = (s) => s.includes('-') && /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(s);
    if (line.includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      closeList();
      const splitRow = (row) => row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
      const headers = splitRow(line);
      i += 2; // consume header + separator
      const bodyRows = [];
      while (i < lines.length && lines[i].includes('|') && !/^\s*$/.test(lines[i])) {
        bodyRows.push(splitRow(lines[i]));
        i += 1;
      }
      const thead = `<thead><tr>${headers.map((h) => `<th>${inline(h)}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${bodyRows
        .map((r) => `<tr>${headers.map((_, idx) => `<td>${inline(r[idx] || '')}</td>`).join('')}</tr>`)
        .join('')}</tbody>`;
      html.push(`<table>${thead}${tbody}</table>`);
      continue;
    }

    // Task list item (`- [ ] todo` / `- [x] done`). Checked BEFORE the plain
    // bullet, whose pattern also matches it. Emitted in the exact shape
    // @lexical/list exports (`__lexicallisttype` on the <ul>, `role` +
    // `aria-checked` on the <li>), which the documents editor imports as a real
    // checklist and sanitizeRichTextHtml keeps — so an AI/MCP-written todo is a
    // clickable checkbox, not the literal text "[ ]".
    const task = line.match(/^\s*[-*+]\s+\[([ xX])\]\s+(.*)$/);
    if (task) {
      if (listType !== 'check') {
        closeList();
        html.push('<ul __lexicallisttype="check">');
        listType = 'check';
      }
      const checked = task[1].toLowerCase() === 'x';
      html.push(`<li role="checkbox" aria-checked="${checked ? 'true' : 'false'}">${inline(task[2])}</li>`);
      i += 1;
      continue;
    }

    // Unordered list item
    const ul = line.match(/^\s*[-*+]\s+(.*)$/);
    if (ul) {
      if (listType !== 'ul') {
        closeList();
        html.push('<ul>');
        listType = 'ul';
      }
      html.push(`<li>${inline(ul[1])}</li>`);
      i += 1;
      continue;
    }

    // Ordered list item
    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (ol) {
      if (listType !== 'ol') {
        closeList();
        html.push('<ol>');
        listType = 'ol';
      }
      html.push(`<li>${inline(ol[1])}</li>`);
      i += 1;
      continue;
    }

    // Paragraph (gather consecutive non-empty, non-structural lines)
    closeList();
    const para = [line];
    i += 1;
    while (
      i < lines.length &&
      !/^\s*$/.test(lines[i]) &&
      !/^```/.test(lines[i]) &&
      !/^(#{1,6})\s/.test(lines[i]) &&
      !/^\s*[-*+]\s/.test(lines[i]) &&
      !/^\s*\d+[.)]\s/.test(lines[i]) &&
      !/^>\s?/.test(lines[i]) &&
      !HTML_BLOCK_START.test(lines[i])
    ) {
      para.push(lines[i]);
      i += 1;
    }
    html.push(`<p>${inline(para.join(' '))}</p>`);
  }

  closeList();
  return html.join('');
}

export default markdownToHtml;
